// ===================================================
// components/Layout/NavPresetEditor.jsx
// Глобальный редактор пресетов / состава rail-хабов.
// ===================================================
import React, { useMemo, useRef, useState } from 'react';
import { NAVIGATION_SYSTEM_PRESETS } from 'config/navigation.presets';
import { DOCK_DISPLAY_MODES, resolveSystemNavPresets } from 'config/navigation.registry';

const NavPresetEditor = ({
  open,
  onClose,
  hubs = [],
  prefs,
  onApplyPreset,
  onSetHubOrder,
  onSetHiddenHubIds,
  onSetDockDisplayMode,
  onReset,
  onSavePersonal,
  onDeletePersonal,
  onExport,
  onImport,
  saving,
}) => {
  const [personalName, setPersonalName] = useState('');
  const [importError, setImportError] = useState(null);
  const fileRef = useRef(null);

  const systemPresets = useMemo(() => {
    const override = resolveSystemNavPresets(prefs?.platform);
    return override || NAVIGATION_SYSTEM_PRESETS;
  }, [prefs?.platform]);

  const visibleIds = useMemo(() => {
    const hidden = new Set(prefs?.hiddenHubIds || []);
    const order = prefs?.hubOrder?.length
      ? prefs.hubOrder
      : hubs.map((h) => h.id);
    const ordered = order.filter((id) => hubs.some((h) => h.id === id));
    hubs.forEach((h) => {
      if (!ordered.includes(h.id)) ordered.push(h.id);
    });
    return ordered.filter((id) => !hidden.has(id));
  }, [hubs, prefs]);

  const hiddenIds = prefs?.hiddenHubIds || [];

  if (!open) return null;

  const move = (hubId, dir) => {
    const list = [...visibleIds];
    const idx = list.indexOf(hubId);
    if (idx < 0) return;
    const next = idx + dir;
    if (next < 0 || next >= list.length) return;
    const tmp = list[idx];
    list[idx] = list[next];
    list[next] = tmp;
    onSetHubOrder?.(list.concat(hiddenIds.filter((id) => !list.includes(id))));
  };

  const toggleHidden = (hubId) => {
    const set = new Set(hiddenIds);
    if (set.has(hubId)) set.delete(hubId);
    else set.add(hubId);
    onSetHiddenHubIds?.([...set]);
  };

  const handleExport = () => {
    try {
      const snapshot = onExport?.();
      if (!snapshot) return;
      const blob = new Blob([JSON.stringify(snapshot, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `markbase-nav-preset-${new Date().toISOString().slice(0, 10)}.json`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      setImportError('Не удалось экспортировать пресет');
    }
  };

  const handleImportFile = async (file) => {
    setImportError(null);
    if (!file) return;
    try {
      const text = await file.text();
      const parsed = JSON.parse(text);
      onImport?.(parsed);
    } catch (err) {
      setImportError(err?.message || 'Некорректный JSON пресета');
    } finally {
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  return (
    <div className="mb-nav-editor-backdrop" role="presentation" onClick={onClose}>
      <div
        className="mb-nav-editor"
        role="dialog"
        aria-modal="true"
        aria-labelledby="mb-nav-editor-title"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mb-nav-editor__header">
          <div>
            <p className="mb-nav-editor__kicker">Меню</p>
            <h2 id="mb-nav-editor-title" className="mb-nav-editor__title">Настроить меню</h2>
            <p className="mb-nav-editor__sub">Пресеты, hover-подменю и состав разделов. Синхронизируется на все устройства.</p>
          </div>
          <button type="button" className="mb-nav-editor__close" onClick={onClose} aria-label="Закрыть">
            ×
          </button>
        </div>

        <div className="mb-nav-editor__body">
          <section className="mb-nav-editor__section">
            <h3 className="mb-nav-editor__section-title">При наведении на раздел</h3>
            <div className="mb-nav-editor__presets">
              {DOCK_DISPLAY_MODES.map((mode) => (
                <button
                  key={mode.id}
                  type="button"
                  className={`mb-nav-editor__preset ${prefs?.dockDisplayMode === mode.id ? 'is-active' : ''}`}
                  onClick={() => onSetDockDisplayMode?.(mode.id)}
                >
                  <span className="mb-nav-editor__preset-label">{mode.label}</span>
                  <span className="mb-nav-editor__preset-desc">{mode.description}</span>
                </button>
              ))}
            </div>
          </section>

          <section className="mb-nav-editor__section">
            <h3 className="mb-nav-editor__section-title">Пресет</h3>
            <div className="mb-nav-editor__presets">
              {systemPresets.map((p) => (
                <button
                  key={p.id}
                  type="button"
                  className={`mb-nav-editor__preset ${prefs?.activePresetId === p.id ? 'is-active' : ''}`}
                  onClick={() => onApplyPreset?.(p.id)}
                >
                  <span className="mb-nav-editor__preset-label">{p.label}</span>
                  <span className="mb-nav-editor__preset-desc">{p.description}</span>
                </button>
              ))}
              {(prefs?.personalPresets || []).map((p) => (
                <div
                  key={p.id}
                  className={`mb-nav-editor__preset mb-nav-editor__preset--personal ${prefs?.activePresetId === `personal:${p.id}` ? 'is-active' : ''}`}
                >
                  <button
                    type="button"
                    className="mb-nav-editor__preset-main"
                    onClick={() => onApplyPreset?.(`personal:${p.id}`)}
                  >
                    <span className="mb-nav-editor__preset-label">{p.name}</span>
                    <span className="mb-nav-editor__preset-desc">Личный пресет</span>
                  </button>
                  <button
                    type="button"
                    className="mb-nav-editor__preset-delete"
                    title="Удалить пресет"
                    aria-label={`Удалить пресет ${p.name}`}
                    disabled={saving}
                    onClick={() => onDeletePersonal?.(p.id)}
                  >
                    ✕
                  </button>
                </div>
              ))}
            </div>
          </section>

          <section className="mb-nav-editor__section">
            <h3 className="mb-nav-editor__section-title">Разделы на панели</h3>
            <ul className="mb-nav-editor__hub-list">
              {hubs.map((hub) => {
                const isHidden = hiddenIds.includes(hub.id);
                return (
                  <li key={hub.id} className={`mb-nav-editor__hub-row ${isHidden ? 'is-hidden' : ''}`}>
                    <label className="mb-nav-editor__hub-check">
                      <input
                        type="checkbox"
                        checked={!isHidden}
                        onChange={() => toggleHidden(hub.id)}
                      />
                      <span>{hub.label}</span>
                    </label>
                    {!isHidden && (
                      <span className="mb-nav-editor__hub-actions">
                        <button type="button" onClick={() => move(hub.id, -1)} aria-label="Выше">↑</button>
                        <button type="button" onClick={() => move(hub.id, 1)} aria-label="Ниже">↓</button>
                      </span>
                    )}
                  </li>
                );
              })}
            </ul>
          </section>

          <section className="mb-nav-editor__section mb-nav-editor__section--row">
            <input
              className="mb-nav-editor__input"
              value={personalName}
              onChange={(e) => setPersonalName(e.target.value)}
              placeholder="Название личного пресета"
              aria-label="Название личного пресета"
            />
            <button
              type="button"
              className="mb-nav-editor__btn"
              disabled={saving || !personalName.trim()}
              onClick={() => {
                onSavePersonal?.(personalName.trim());
                setPersonalName('');
              }}
            >
              Сохранить как пресет
            </button>
          </section>

          <section className="mb-nav-editor__section mb-nav-editor__section--row">
            <button type="button" className="mb-nav-editor__btn" onClick={handleExport} disabled={saving}>
              Экспорт JSON
            </button>
            <button
              type="button"
              className="mb-nav-editor__btn"
              disabled={saving}
              onClick={() => fileRef.current?.click()}
            >
              Импорт JSON
            </button>
            <input
              ref={fileRef}
              type="file"
              accept="application/json,.json"
              hidden
              onChange={(e) => handleImportFile(e.target.files?.[0])}
            />
          </section>
          {importError ? (
            <p className="mb-nav-editor__sub" role="alert" style={{ color: 'var(--danger, #b91c1c)' }}>
              {importError}
            </p>
          ) : null}
        </div>

        <div className="mb-nav-editor__footer">
          <button type="button" className="mb-nav-editor__btn mb-nav-editor__btn--ghost" onClick={onReset} disabled={saving}>
            Сбросить по умолчанию
          </button>
          <button type="button" className="mb-nav-editor__btn mb-nav-editor__btn--primary" onClick={onClose}>
            Готово
          </button>
        </div>
      </div>
    </div>
  );
};

export default NavPresetEditor;
